import { useState } from 'react';
import type { JSX } from 'react';

import type { PrefetchedDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type { PetitionHeadlineBanditData, Sharer } from 'src/app/pages/petitionDetails/shared/types';

import { ShareHeadlineProvider, useDefaultShareHeadline } from '../../hooks/shareHeadline';
import { getHeadlineBanditVariantText, getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';

type PrefetchedData = Readonly<{
	sharer?: Sharer;
	headlineVariant?: PetitionHeadlineBanditData;
}>;

const ALLOWED_SHARE_TYPES = ['wa', 'cl_', 'fb', 'tw'];

export const PetitionDetailsBanditV2 = withPrefetchedData(
	({ prefetchedData }: PrefetchedDataProps<PrefetchedData>): JSX.Element => {
		// this allows us to conserve the sharer after first render
		const [sharer] = useState<Sharer | undefined>(prefetchedData?.sharer);
		const [headlineVariant] = useState<PetitionHeadlineBanditData | undefined>(prefetchedData?.headlineVariant);

		const { title, description } = useDefaultShareHeadline();

		if (!sharer) {
			return <CommonPetitionDetails />;
		}

		if (!headlineVariant) {
			// uses default share headline data
			return (
				<ShareHeadlineProvider sharer={sharer} title={title} description={description}>
					<CommonPetitionDetails />
				</ShareHeadlineProvider>
			);
		}

		return (
			<ShareHeadlineProvider sharer={sharer} title={headlineVariant.headline} description={headlineVariant.description}>
				<CommonPetitionDetails />
			</ShareHeadlineProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [CommonPetitionDetails],
		prefetchData: async (context) => {
			const {
				params: { slug, shareType, experimentName, experimentVariantName, sharerUserId },
			} = context;

			if (!(slug && experimentVariantName && experimentName && sharerUserId)) return {};
			if (!(shareType && ALLOWED_SHARE_TYPES.includes(shareType))) return {};

			return {
				headlineVariant: await getHeadlineBanditVariantText(
					shareType,
					slug,
					experimentVariantName,
					context.utilityContext,
				).catch(() => undefined),
				sharer: await getSharer(sharerUserId, context.utilityContext).catch(() => undefined),
			};
		},
	},
);
